/**
 * HandoffReceiver
 *
 * Screen shown on the receiving device when a handoff offer arrives.
 * Displays call details and accept/decline buttons.
 */
import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { acceptHandoff, declineHandoff } from '../actions';
const OVERLAY_STYLE = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2000,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
};
const CARD_STYLE = {
    background: '#1e1e2e',
    borderRadius: 16,
    padding: '40px 48px',
    maxWidth: 420,
    width: '90%',
    textAlign: 'center',
    color: '#fff',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
};
const TITLE_STYLE = {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 8,
    color: '#e0e0e0'
};
const SUBTITLE_STYLE = {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
    marginBottom: 32
};
const DETAIL_ROW_STYLE = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    fontSize: 14
};
const BUTTON_ROW_STYLE = {
    display: 'flex',
    gap: 12,
    marginTop: 32
};
const ACCEPT_BUTTON_STYLE = {
    flex: 1,
    background: '#4caf50',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s'
};
const DECLINE_BUTTON_STYLE = {
    flex: 1,
    background: 'transparent',
    color: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    padding: '14px 20px',
    fontSize: 15,
    cursor: 'pointer',
    transition: 'background 0.2s'
};
const HandoffReceiver = ({ fromDeviceName, roomName }) => {
    const dispatch = useDispatch();
    const receivedToken = useSelector((state) => state['features/device-handoff']?.receivedToken);
    const receivedRoomName = useSelector((state) => state['features/device-handoff']?.receivedRoomName);
    const handleAccept = useCallback(() => {
        if (receivedToken) {
            dispatch(acceptHandoff(receivedToken));
        }
    }, [dispatch, receivedToken]);
    const handleDecline = useCallback(() => {
        if (receivedToken) {
            dispatch(declineHandoff(receivedToken));
        }
    }, [dispatch, receivedToken]);
    if (!receivedToken) {
        return null;
    }
    return (React.createElement("div", { style: OVERLAY_STYLE },
        React.createElement("div", { style: CARD_STYLE },
            React.createElement("div", { style: TITLE_STYLE }, "Call moving here"),
            React.createElement("div", { style: SUBTITLE_STYLE }, "A call is moving to this device"),
            React.createElement("div", { style: { textAlign: 'left', marginBottom: 8 } },
                fromDeviceName && (React.createElement("div", { style: DETAIL_ROW_STYLE },
                    React.createElement("span", { style: { color: 'rgba(255,255,255,0.5)' } }, "From"),
                    React.createElement("span", null, fromDeviceName))),
                React.createElement("div", { style: DETAIL_ROW_STYLE },
                    React.createElement("span", { style: { color: 'rgba(255,255,255,0.5)' } }, "Room"),
                    React.createElement("span", null, receivedRoomName || roomName || '—'))),
            React.createElement("div", { style: BUTTON_ROW_STYLE },
                React.createElement("button", { onClick: handleAccept, style: ACCEPT_BUTTON_STYLE, type: 'button' }, "Accept move"),
                React.createElement("button", { onClick: handleDecline, style: DECLINE_BUTTON_STYLE, type: 'button' }, "Decline")))));
};
export default HandoffReceiver;
